import { useEffect, useState, useCallback, useRef } from 'react'
import { Sparkles, Plus, Edit3, Trash2, UserPlus, Search, X, Upload } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import PageHeader from '@/components/PageHeader'
import { profileItemsAPI, playersAPI, teamsAPI, badgeAPI } from '@/lib/api'
import ImageCropper from '@/components/ImageCropper'
import { RARITIES, rarityColor } from '@/lib/rarity'
import { cn } from '@/lib/utils'

/* Admin-catalogus van profielitems (banners, avatar-frames, accent-kleuren).
   Maak items aan, koppel ze aan een naam + rariteit + herkomst-hint, en ken
   ze toe aan users (of trek ze in). */

const KINDS = [
  { k: 'banner',       label: 'Banner',       hint: 'Foto/URL bovenaan het profiel' },
  { k: 'avatar_frame', label: 'Avatar-kader', hint: 'Kleur + stijl rond de avatar' },
  { k: 'accent',       label: 'Accent-kleur', hint: 'Overschrijft de hero-accent' },
]
// Frame-catalogus komt uit ProfileFrame — nieuwe animated variants (lightning,
// aurora, cosmic, ice, plasma, void) verschijnen hier automatisch. FRAME_META
// bevat rariteit + label voor de admin-picker.
import { FRAME_STYLES, FRAME_META } from '@/components/profile/ProfileFrame'
const BANNER_EFFECTS = [
  { v: '',         label: '— geen effect (statisch) —' },
  { v: 'shimmer',  label: 'Shimmer — glans veegt horizontaal' },
  { v: 'aurora',   label: 'Aurora — kleurige gloed schuift traag' },
  { v: 'scan',     label: 'Scanlines — CRT-esthetiek met sweep' },
  { v: 'pulse',    label: 'Pulse — top-glow ademt in en uit' },
  { v: 'confetti', label: 'Confetti — gekleurde vlekjes vallen door de banner' },
  { v: 'snow',     label: 'Snow — witte dots dwarrelen (winter)' },
]
const BADGE_ANIMATIONS = [
  { v: '',      label: '— statisch —' },
  { v: 'shine', label: 'Shine — glans-band veegt over de kaart' },
  { v: 'pulse', label: 'Pulse — icoon schaalt subtiel + glow' },
  { v: 'glow',  label: 'Glow — brede halo ademt in en uit' },
  { v: 'orbit', label: 'Orbit — dashed ring draait rond icoon' },
]

export default function ProfileItemsAdminPage() {
  const [items, setItems] = useState([])
  const [dlg, setDlg] = useState(null)
  const [grantDlg, setGrantDlg] = useState(null)
  const [zoekQ, setZoekQ] = useState('')
  const [users, setUsers] = useState([])
  const [teams, setTeams] = useState([])
  const [grantMode, setGrantMode] = useState('user')   // 'user' | 'team'
  const [holders, setHolders] = useState({ users: [], teams: [] })
  const [err, setErr] = useState(null)

  const laadHolders = useCallback(async (id) => {
    try { const r = await profileItemsAPI.holders(id); setHolders(r.data || { users: [], teams: [] }) }
    catch { setHolders({ users: [], teams: [] }) }
  }, [])

  const laad = useCallback(() => {
    profileItemsAPI.list().then(r => setItems(r.data?.items ?? []))
      .catch(e => setErr(e.response?.data?.error ?? e.message))
  }, [])
  useEffect(() => { laad() }, [laad])

  useEffect(() => {
    if (!grantDlg) return
    laadHolders(grantDlg.id)
    if (grantMode === 'user') {
      const t = setTimeout(() => {
        playersAPI.list(zoekQ).then(r => setUsers(r.data?.players ?? r.data?.users ?? [])).catch(() => setUsers([]))
      }, 200)
      return () => clearTimeout(t)
    } else {
      teamsAPI.list().then(r => setTeams(r.data?.teams ?? [])).catch(() => setTeams([]))
    }
  }, [grantDlg, grantMode, zoekQ, laadHolders])

  const bewaar = async (e) => {
    e.preventDefault()
    setErr(null)
    const body = {
      name: dlg.name, kind: dlg.kind, config: dlg.config || {},
      description: dlg.description, source_hint: dlg.source_hint, rarity: dlg.rarity,
      on_badge_id: dlg.on_badge_id ?? null,
    }
    try {
      if (dlg.id) await profileItemsAPI.update(dlg.id, body)
      else        await profileItemsAPI.create(body)
      setDlg(null); laad()
    } catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }
  const wis = async (id) => {
    if (!window.confirm('Item verwijderen? Alle toekenningen aan users gaan mee.')) return
    try { await profileItemsAPI.remove(id); laad() }
    catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }
  const grantUser = async (uid) => {
    try {
      await profileItemsAPI.grant(grantDlg.id, uid)
      // Dialoog blijft open zodat je meteen kan zien dat de toekenning geland
      // is en eventueel nog een tweede grant kan doen. Sluiten met "Sluit".
      await laadHolders(grantDlg.id)
    } catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }
  const grantTeam = async (tid) => {
    try {
      await profileItemsAPI.grantTeam(grantDlg.id, tid)
      await laadHolders(grantDlg.id)
    } catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }
  const revokeUser = async (uid) => {
    if (!window.confirm('Deze toekenning intrekken?')) return
    try {
      await profileItemsAPI.revoke(grantDlg.id, uid)
      await laadHolders(grantDlg.id)
    } catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }
  const revokeTeamGrant = async (tid) => {
    if (!window.confirm('Deze team-toekenning intrekken?')) return
    try {
      await profileItemsAPI.revokeTeam(grantDlg.id, tid)
      await laadHolders(grantDlg.id)
    } catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }

  const perKind = { banner: [], avatar_frame: [], accent: [] }
  for (const it of items) if (perKind[it.kind]) perKind[it.kind].push(it)

  return (
    <div>
      <PageHeader icon={Sparkles} title="Profielitems"
                  subtitle="Banners, avatar-kaders en accent-kleuren als beloning" />

      {err && <p className="text-sm text-signal mb-3">{err}</p>}

      {KINDS.map(({ k, label, hint }) => (
        <Card key={k} className="mb-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              {label} <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">· {perKind[k].length}</span>
              <p className="text-xs text-muted-foreground font-normal mt-1">{hint}</p>
            </CardTitle>
            <Button size="sm" onClick={() => setDlg({ name: '', kind: k, config: defaultConfig(k),
                                                       description: '', source_hint: '', rarity: 'common' })}>
              <Plus className="h-4 w-4 mr-1" /> Nieuw
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {perKind[k].length === 0 && <p className="text-sm text-muted-foreground">Nog geen {label.toLowerCase()}.</p>}
            {perKind[k].map(it => {
              const rk = rarityColor(it.rarity)
              return (
                <div key={it.id} className="flex items-center gap-3 rounded-[4px] border border-border bg-card px-3 py-2"
                     style={{ borderLeft: `3px solid ${rk}` }}>
                  <ItemPreview item={it} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{it.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: rk }}>
                      {it.rarity}{it.source_hint ? ` · ${it.source_hint}` : ''}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setGrantDlg(it)}>
                    <UserPlus className="h-3.5 w-3.5 mr-1" /> Toekennen
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                          onClick={() => setDlg({ ...it, config: it.config || {} })}>
                    <Edit3 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => wis(it.id)}>
                    <Trash2 className="h-4 w-4 text-signal" />
                  </Button>
                </div>
              )
            })}
          </CardContent>
        </Card>
      ))}

      {dlg && <ItemDialog dlg={dlg} setDlg={setDlg} onSubmit={bewaar} />}

      {grantDlg && (
        <Dialog open onOpenChange={(o) => !o && setGrantDlg(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ken "{grantDlg.name}" toe aan…</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {(holders.users?.length > 0 || holders.teams?.length > 0) && (
                <div className="rounded-[4px] border border-border bg-card/50 p-2 space-y-1">
                  <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground px-1">
                    Reeds toegewezen aan
                  </div>
                  {holders.users?.map(u => (
                    <div key={`u-${u.id}`}
                         className="flex items-center gap-2 rounded-[3px] px-2 py-1.5 hover:bg-background">
                      <div className="h-6 w-6 rounded-full bg-muted grid place-items-center shrink-0 text-[10px] font-semibold">
                        {(u.display_name || u.username || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold truncate">{u.display_name || u.username}</div>
                        <div className="font-mono text-[10px] text-muted-foreground truncate">
                          @{u.username}{u.source ? ` · ${u.source}` : ''}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-signal hover:text-signal"
                              onClick={() => revokeUser(u.id)}>
                        <X className="h-3.5 w-3.5 mr-1" /> Intrekken
                      </Button>
                    </div>
                  ))}
                  {holders.teams?.map(t => (
                    <div key={`t-${t.id}`}
                         className="flex items-center gap-2 rounded-[3px] px-2 py-1.5 hover:bg-background"
                         style={{ borderLeft: `2px solid ${t.primary_color || '#B4FF39'}` }}>
                      {t.logo_url
                        ? <img src={t.logo_url} alt="" className="h-6 w-6 rounded-[3px] object-cover shrink-0" />
                        : <div className="h-6 w-6 rounded-[3px] shrink-0" style={{ background: `${t.primary_color || '#B4FF39'}22` }} />}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold truncate">{t.name}</div>
                        <div className="font-mono text-[10px] text-muted-foreground truncate">
                          team{t.source ? ` · ${t.source}` : ''}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-signal hover:text-signal"
                              onClick={() => revokeTeamGrant(t.id)}>
                        <X className="h-3.5 w-3.5 mr-1" /> Intrekken
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setGrantMode('user')}
                        className={`rounded-md border p-2 text-sm ${grantMode === 'user' ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'}`}>
                  Een user
                </button>
                <button type="button" onClick={() => setGrantMode('team')}
                        className={`rounded-md border p-2 text-sm ${grantMode === 'team' ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'}`}>
                  Een team
                </button>
              </div>

              {grantMode === 'user' && (
                <>
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={zoekQ} onChange={e => setZoekQ(e.target.value)} autoFocus
                           placeholder="Zoek user…" className="pl-8" />
                  </div>
                  <div className="max-h-[24rem] overflow-y-auto space-y-1">
                    {users.slice(0, 30).map(u => {
                      const alHouder = holders.users?.some(h => h.id === u.id)
                      return (
                        <button key={u.id} type="button" disabled={alHouder}
                                onClick={() => !alHouder && grantUser(u.id)}
                                className={cn(
                                  'w-full flex items-center gap-3 rounded-[4px] border bg-card px-3 py-2 text-left transition-colors',
                                  alHouder ? 'border-border/30 opacity-50 cursor-not-allowed' : 'border-border/40 hover:border-acid/60'
                                )}>
                          <div className="h-8 w-8 rounded-full bg-muted grid place-items-center shrink-0 font-semibold text-xs">
                            {(u.display_name || u.username || '?').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{u.display_name || u.username}</div>
                            <div className="font-mono text-[10px] text-muted-foreground truncate">@{u.username}</div>
                          </div>
                          {alHouder && (
                            <span className="font-mono text-[9px] uppercase tracking-widest text-live/70 shrink-0">
                              Al toegewezen
                            </span>
                          )}
                        </button>
                      )
                    })}
                    {users.length === 0 && <p className="text-sm text-muted-foreground p-2">Geen user gevonden.</p>}
                  </div>
                </>
              )}

              {grantMode === 'team' && (
                <div className="max-h-[24rem] overflow-y-auto space-y-1">
                  {teams.length === 0 && <p className="text-sm text-muted-foreground p-2">Nog geen teams.</p>}
                  {teams.map(t => {
                    const alHouder = holders.teams?.some(h => h.id === t.id)
                    return (
                      <button key={t.id} type="button" disabled={alHouder}
                              onClick={() => !alHouder && grantTeam(t.id)}
                              className={cn(
                                'w-full flex items-center gap-3 rounded-[4px] border bg-card px-3 py-2 text-left transition-colors',
                                alHouder ? 'border-border/30 opacity-50 cursor-not-allowed' : 'border-border/40 hover:border-acid/60'
                              )}
                              style={{ borderLeft: `3px solid ${t.primary_color || '#B4FF39'}` }}>
                        {t.logo_url
                          ? <img src={t.logo_url} alt="" className="h-8 w-8 rounded-[3px] object-cover shrink-0" />
                          : <div className="h-8 w-8 rounded-[3px] shrink-0" style={{ background: `${t.primary_color || '#B4FF39'}22` }} />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{t.name}</div>
                          <div className="font-mono text-[10px] text-muted-foreground truncate">{t.members_count} leden</div>
                        </div>
                        {alHouder && (
                          <span className="font-mono text-[9px] uppercase tracking-widest text-live/70 shrink-0">
                            Al toegewezen
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setGrantDlg(null)}>Sluit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

function defaultConfig(kind) {
  if (kind === 'banner')       return { image_url: '' }
  if (kind === 'avatar_frame') return { color: '#B4FF39', style: 'ring' }
  if (kind === 'accent')       return { color: '#B4FF39' }
  return {}
}

function BannerImageVeld({ c, setConfig }) {
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [cropSrc, setCropSrc] = useState(null)
  const [cropOpen, setCropOpen] = useState(false)

  const kies = (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    if (f.size > 5 * 1024 * 1024) { setErr('Bestand mag max 5MB zijn'); return }
    setErr(null)
    setCropSrc(URL.createObjectURL(f))
    setCropOpen(true)
  }
  const uploadCropped = async (blob) => {
    setBusy(true); setErr(null)
    try {
      const fd = new FormData()
      fd.append('image', new File([blob], 'banner.jpg', { type: 'image/jpeg' }))
      const r = await profileItemsAPI.uploadImage(fd)
      setConfig({ image_url: r.data.url })
    } catch (e2) { setErr(e2.response?.data?.error || e2.message) }
    finally { setBusy(false); setCropOpen(false) }
  }
  return (
    <div className="space-y-2">
      <Label>Banner-afbeelding</Label>
      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={kies} />
        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy}>
          <Upload className="h-3 w-3 mr-1" /> {busy ? 'Uploaden…' : 'Uploaden en bijsnijden'}
        </Button>
        <span className="text-xs text-muted-foreground">of plak een URL:</span>
      </div>
      <Input value={c.image_url || ''} onChange={e => setConfig({ image_url: e.target.value })}
             placeholder="https://…/banner.jpg of /uploads/profile-items/…" />
      {err && <p className="text-xs text-signal">{err}</p>}
      {c.image_url && (
        <div className="h-24 w-full rounded-[4px] bg-cover bg-center border border-border"
             style={{ backgroundImage: `url("${c.image_url}")` }} />
      )}
      <p className="text-xs text-muted-foreground">Formaat: liefst 1500×500 (3:1). Wordt over de profiel-hero gelegd.</p>

      <div className="pt-2">
        <Label>Bewegend effect (optioneel)</Label>
        <select value={c.effect || ''} onChange={e => setConfig({ effect: e.target.value || undefined })}
                className="w-full h-9 rounded-[4px] border border-border bg-background px-2 text-sm">
          {BANNER_EFFECTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <p className="text-xs text-muted-foreground mt-1">
          Alleen zichtbaar wanneer deze banner geëquipeerd is uit de inventaris (niet bij eigen uploads).
          Respecteert <code>prefers-reduced-motion</code>.
        </p>
      </div>

      <ImageCropper open={cropOpen} imageSrc={cropSrc}
                    onClose={() => setCropOpen(false)}
                    onCropComplete={uploadCropped}
                    aspectRatio={3}
                    shape="rect"
                    outputSize={{ width: 1500, height: 500 }}
                    title="Banner bijsnijden" />
    </div>
  )
}

function ItemPreview({ item }) {
  const c = item.config || {}
  if (item.kind === 'banner') {
    return c.image_url
      ? <div className="h-10 w-16 rounded-[3px] bg-cover bg-center shrink-0" style={{ backgroundImage: `url("${c.image_url}")` }} />
      : <div className="h-10 w-16 rounded-[3px] bg-muted grid place-items-center shrink-0 pf-mono text-[9px] text-muted-foreground">geen</div>
  }
  if (item.kind === 'avatar_frame') {
    const style = c.style || 'ring'
    const color = c.color || '#B4FF39'
    const wrap = style === 'dashed'
      ? { boxShadow: `inset 0 0 0 3px ${color}`, outline: `2px dashed ${color}88`, outlineOffset: '2px', padding: 0 }
      : { background: `linear-gradient(135deg, ${color}, transparent 50%)`, padding: 3 }
    return (
      <div className="h-10 w-10 rounded-full shrink-0" style={{ ...wrap, borderRadius: '9999px' }}>
        <div className="w-full h-full rounded-full bg-background" />
      </div>
    )
  }
  return <div className="h-10 w-10 rounded-full shrink-0" style={{ background: c.color || '#94a3b8' }} />
}

function ItemDialog({ dlg, setDlg, onSubmit }) {
  const c = dlg.config || {}
  const setConfig = (partial) => setDlg({ ...dlg, config: { ...c, ...partial } })
  const [badgeTypes, setBadgeTypes] = useState([])
  useEffect(() => {
    badgeAPI.getAllTypes().then(r => setBadgeTypes(r.data?.badges ?? [])).catch(() => {})
  }, [])
  return (
    <Dialog open onOpenChange={(o) => !o && setDlg(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{dlg.id ? 'Item bewerken' : 'Nieuw item'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Naam *</Label>
              <Input value={dlg.name} required
                     onChange={e => setDlg({ ...dlg, name: e.target.value })}
                     placeholder="Kempenvision winnaar 2026" />
            </div>
            <div>
              <Label>Rariteit</Label>
              <select value={dlg.rarity} onChange={e => setDlg({ ...dlg, rarity: e.target.value })}
                      className="w-full h-9 rounded-[4px] border border-border bg-background px-2 text-sm">
                {RARITIES.map(r => <option key={r.r} value={r.r}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label>Soort</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {KINDS.map(({ k, label }) => (
                <button key={k} type="button"
                        onClick={() => setDlg({ ...dlg, kind: k, config: defaultConfig(k) })}
                        className={`rounded-[4px] border p-2 text-sm ${dlg.kind === k ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {dlg.kind === 'banner' && (
            <BannerImageVeld c={c} setConfig={setConfig} />
          )}
          {dlg.kind === 'avatar_frame' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kleur</Label>
                <input type="color" value={c.color || '#B4FF39'} onChange={e => setConfig({ color: e.target.value })}
                       className="h-9 w-full rounded-[4px] border border-border cursor-pointer bg-transparent" />
              </div>
              <div>
                <Label>Stijl</Label>
                <select value={c.style || 'ring'} onChange={e => setConfig({ style: e.target.value })}
                        className="w-full h-9 rounded-[4px] border border-border bg-background px-2 text-sm">
                  {FRAME_STYLES.map(s => {
                    const meta = FRAME_META[s] || { label: s, rarity: '', animated: false }
                    return (
                      <option key={s} value={s}>
                        {meta.label}{meta.animated ? ' · animated' : ''}{meta.rarity ? ` · ${meta.rarity}` : ''}
                      </option>
                    )
                  })}
                </select>
              </div>
            </div>
          )}
          {dlg.kind === 'accent' && (
            <div>
              <Label>Kleur</Label>
              <input type="color" value={c.color || '#B4FF39'} onChange={e => setConfig({ color: e.target.value })}
                     className="h-9 w-full rounded-[4px] border border-border cursor-pointer bg-transparent" />
            </div>
          )}

          <div>
            <Label>Auto-toekennen bij badge (optioneel)</Label>
            <select value={dlg.on_badge_id ?? ''}
                    onChange={e => setDlg({ ...dlg, on_badge_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full h-9 rounded-[4px] border border-border bg-background px-2 text-sm">
              <option value="">— Geen auto-koppeling —</option>
              {badgeTypes.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Zodra iemand deze badge krijgt (via winnaar-hook of handmatig), krijgt hij dit item automatisch in z'n inventaris.
            </p>
          </div>
          <div>
            <Label>Herkomst (tekst, optioneel)</Label>
            <Input value={dlg.source_hint || ''} onChange={e => setDlg({ ...dlg, source_hint: e.target.value })}
                   placeholder="Winnaar Kempenvision 2026" />
          </div>
          <div>
            <Label>Omschrijving (optioneel)</Label>
            <Input value={dlg.description || ''} onChange={e => setDlg({ ...dlg, description: e.target.value })} />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDlg(null)}>Annuleer</Button>
            <Button type="submit">{dlg.id ? 'Opslaan' : 'Aanmaken'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
